import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import {
  ORGANIZATION_MEMBERSHIP_CODE_GROUP_SIZES as GROUP_SIZES,
  ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS as TOTAL_DIGITS,
} from '@/components/ui/formats/organizationMembershipCode';
import OrganizationMembershipCodeGroups from '@/components/ui/formats/OrganizationMembershipCodeGroups';
import Input from '@/components/ui/forms/Input';

import styles from './OrganizationMembershipCodeSplitInput.module.scss';

interface Props {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  name?: string;
  className?: string;
}

const toCells = (value: string): string[] =>
  Array.from({ length: TOTAL_DIGITS }, (_, i) => value[i] ?? '');

const OrganizationMembershipCodeSplitInput: React.FC<Props> = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  name,
  className,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>(
    Array<HTMLInputElement | null>(TOTAL_DIGITS).fill(null),
  );

  // 各マスの値を内部で保持する。空きマスを保持してバックスペース時に桁が左へずれないようにしつつ、
  // 外部 onChange へは数字のみのクリーンな値を渡し、内部都合（空きマスの位置）を漏らさない。
  const [cells, setCells] = useState<string[]>(() => toCells(value));

  // IME 変換中のマスと、変換を始める前のそのマスの値。同時に変換できるマスは 1 つだけ。
  // https://github.com/quipper/school-comm-issues/issues/7554
  const compositionRef = useRef<{ index: number; before: string } | null>(null);

  // 確定直後に届く非変換の input を捨てるための目印。Chrome は compositionend のあとに
  // 確定値の input を続けて発火するため、そのまま処理すると桁送りが 2 回走る。
  // Firefox のように確定後の input が来ないブラウザでは目印が残るが、次の handleChange が
  // 一致判定より先に無条件で捨てるため 1 回分しか生き延びない。
  const committedRef = useRef<{ index: number; value: string } | null>(null);

  // 親が value を変更したとき（リセット等）に追従する。自分が emit した値には反応しない。
  useEffect(() => {
    setCells((prev) => (value === prev.join('') ? prev : toCells(value)));
  }, [value]);

  const focusInput = useCallback((index: number) => {
    const clamped = Math.min(Math.max(index, 0), TOTAL_DIGITS - 1);
    inputRefs.current[clamped]?.focus();
  }, []);

  const updateCells = useCallback(
    (next: string[]) => {
      setCells(next);
      onChange(next.join(''));
    },
    [onChange],
  );

  // ネイティブ beforeinput リスナ（マウント時に一度だけ張る）から常に最新の値・更新関数を
  // 参照するための ref。リスナを張り直さずに済ませ、再アタッチのちらつきを避ける。
  const cellsRef = useRef(cells);
  cellsRef.current = cells;
  const updateCellsRef = useRef(updateCells);
  updateCellsRef.current = updateCells;

  // iOS Safari(WebKit) はソフトキーボードからの入力で keydown.preventDefault() を無視するため、
  // keydown で「上書き＋桁送り」すると、フォーカス移動後に native の挿入が次マスへ届いて二重入力になる
  // （①②→「2」で ①②②、→「3」で ①②③③）。beforeinput は WebKit でも preventDefault で挿入を
  // 確実にキャンセルできるので、1 桁の通常入力(insertText)はここで横取りして上書き＋桁送りする。
  // React18 の合成 onBeforeInput は textInput ベースで native beforeinput と別物のため、ref に直接張る。
  // IME 変換中(isComposing)・複数文字(オートフィル等)は素通りさせ onChange(handleChange) に委ねる。
  // https://github.com/quipper/school-comm-issues/issues/7490
  useEffect(() => {
    const entries = inputRefs.current.map((input, index) => {
      if (!input) return null;
      const handleBeforeInput = (e: Event) => {
        const inputEvent = e as InputEvent;
        if (
          inputEvent.inputType !== 'insertText' ||
          inputEvent.isComposing ||
          compositionRef.current !== null ||
          inputEvent.data === null ||
          !/^[0-9]$/.test(inputEvent.data)
        ) {
          return;
        }
        e.preventDefault();
        const next = [...cellsRef.current];
        next[index] = inputEvent.data;
        updateCellsRef.current(next);
        focusInput(index + 1);
      };
      input.addEventListener('beforeinput', handleBeforeInput);
      return { input, handleBeforeInput };
    });
    return () => {
      entries.forEach((entry) => {
        entry?.input.removeEventListener(
          'beforeinput',
          entry.handleBeforeInput,
        );
      });
    };
  }, [focusInput]);

  // 1 回で複数文字が届くケース（IME 変換確定・オートフィル・ペースト）を各マスへ配る。
  // 数字以外はバリデーション（使用できません）で拾うため、ここでは除去しない。
  const applyInput = useCallback(
    (
      index: number,
      rawValue: string,
      previous: string,
      moveFocus: boolean = true,
    ) => {
      const next = [...cellsRef.current];
      const chars = [...rawValue];

      if (chars.length === 0) {
        next[index] = '';
        updateCellsRef.current(next);
        return;
      }

      // 埋まったマスを IME 等で上書きすると、既存文字を含む複数文字が一度に届く
      // （マスに maxLength を設けていない。理由は下記 Input の注記参照）。そのまま配ると
      // 既存文字が隣のマスへこぼれるため、上書き時は末尾 1 文字だけを採用して桁ずれさせない。
      // https://github.com/quipper/school-comm-issues/issues/7490
      if (previous !== '') {
        next[index] = chars[chars.length - 1];
        updateCellsRef.current(next);
        if (moveFocus) focusInput(index + 1);
        return;
      }

      // 空マスへの複数文字（オートフィル等）のみ、先頭から各マスへ順番に配る。
      let cursor = index;
      for (const char of chars) {
        if (cursor >= TOTAL_DIGITS) break;
        next[cursor] = char;
        cursor += 1;
      }
      updateCellsRef.current(next);
      if (moveFocus) focusInput(cursor);
    },
    [focusInput],
  );

  const handleChange = useCallback(
    (index: number, rawValue: string) => {
      // 変換中は届いた値をそのままマスに持たせる。切り詰めると controlled な value の
      // 書き戻しで変換が打ち切られ、桁送りするとフォーカス先のマスへ確定文字が届いて
      // 二重入力になる。確定するまで外部へも渡さない。
      // https://github.com/quipper/school-comm-issues/issues/7554
      if (compositionRef.current) {
        setCells((prev) => {
          const next = [...prev];
          next[index] = rawValue;
          return next;
        });
        return;
      }

      const committed = committedRef.current;
      committedRef.current = null;
      if (committed?.index === index && committed.value === rawValue) {
        return;
      }

      applyInput(index, rawValue, cellsRef.current[index]);
    },
    [applyInput],
  );

  const handleCompositionStart = useCallback((index: number) => {
    compositionRef.current = { index, before: cellsRef.current[index] };
  }, []);

  const handleCompositionEnd = useCallback(
    (index: number, e: React.CompositionEvent<HTMLInputElement>) => {
      const before = compositionRef.current?.before ?? '';
      compositionRef.current = null;
      const confirmed = e.currentTarget.value;
      committedRef.current = { index, value: confirmed };

      // 変換をキャンセルすると変換開始前の値に戻って compositionend が発火する。
      // 桁送りせず、変換中に抱えていた文字だけを捨てる。
      if (confirmed === before) {
        setCells((prev) => {
          const next = [...prev];
          next[index] = before;
          return next;
        });
        return;
      }

      // 変換中のマスは変換途中の文字を抱えているので previous は必ず非空になり、末尾 1 文字を
      // 採る側へ入る。空マスへ配る経路は変換確定では通さない。通すと「まとめて確定したときだけ
      // 次マス以降が上書きされる」という、同じ操作で結果が変わる状態になる。
      applyInput(index, confirmed, cellsRef.current[index]);
    },
    [applyInput],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (cells[index] === '') {
          if (index > 0) {
            // 空マスでの Backspace は前マスを消してそこへ移動する。
            // 戻り先を必ず空マスにすることで、戻る前と同じ数字を再入力しても値が変化し
            // onChange が発火して桁送りされる。keydown に key が載らない一部モバイル
            // （Android IME の keyCode 229 等）でも成立する経路を確保する。
            // https://github.com/quipper/school-comm-issues/issues/7490
            e.preventDefault();
            const next = [...cells];
            next[index - 1] = '';
            updateCells(next);
            focusInput(index - 1);
          }
        } else {
          e.preventDefault();
          const next = [...cells];
          next[index] = '';
          updateCells(next);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < TOTAL_DIGITS - 1) {
        e.preventDefault();
        focusInput(index + 1);
      }
      // 文字入力は keydown では扱わない。1 桁の通常入力は beforeinput で上書き＋桁送りし、
      // IME 変換確定・オートフィル・複数文字は onChange(handleChange) が担う。keydown で文字を
      // 書くと、iOS WebKit が preventDefault を無視して二重入力になり、また IME 変換の 1 キー目
      // （変換開始前で isComposing=false のことがある）を横取りして桁こぼれさせてしまう。
      // https://github.com/quipper/school-comm-issues/issues/7490
    },
    [cells, updateCells, focusInput],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      // 区切り文字などの数字以外を除去してから切り取る。
      // 先に切り取ると "123-456-7890" のような区切り付きコードで末尾の桁が欠落する。
      const pastedText = e.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, TOTAL_DIGITS);
      // 数字を含まない貼り付けは無視する。toCells('') で全マスを空にして入力済みコードを消さない。
      if (pastedText.length === 0) {
        return;
      }
      updateCells(toCells(pastedText));
      focusInput(Math.min(pastedText.length, TOTAL_DIGITS - 1));
    },
    [updateCells, focusInput],
  );

  // マス間の自動フォーカス移動では発火させず、入力欄全体からフォーカスが外れたときだけ onBlur を呼ぶ
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // 一部の環境（Android IME 等）では compositionend が来ないことがある。変換中のまま
      // フォーカスが外れたら確定として扱い、マスが変換途中の文字を抱えたままになるのを防ぐ。
      // https://github.com/quipper/school-comm-issues/issues/7554
      const composition = compositionRef.current;
      if (composition) {
        compositionRef.current = null;
        applyInput(
          composition.index,
          e.currentTarget.value,
          cellsRef.current[composition.index],
          false,
        );
      }

      if (inputRefs.current.includes(e.relatedTarget as HTMLInputElement)) {
        return;
      }
      onBlur?.();
    },
    [onBlur, applyInput],
  );

  const renderInputGroup = (
    startIndex: number,
    count: number,
    groupIndex: number,
  ) => {
    const inputs = [];
    for (let i = 0; i < count; i++) {
      const index = startIndex + i;
      inputs.push(
        <Input
          key={index}
          innerRef={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          value={cells[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onCompositionStart={() => handleCompositionStart(index)}
          onCompositionEnd={(e) => handleCompositionEnd(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onBlur={handleBlur}
          disabled={disabled}
          // maxLength={1} は付けない。満杯マスでは iOS WebKit が beforeinput(insertText) を
          // 発火させず、上書き＋桁送りができなくなる（既存文字を消さないと再入力できない）。
          // 1 マス 1 文字の制御は value(controlled) と handleChange/beforeinput 側で担保している。
          // https://github.com/quipper/school-comm-issues/issues/7516
          name={name ? `${name}_${index}` : undefined}
          className={styles.digitInput}
          aria-label={`団体会員コード ${index + 1}桁目`}
        />,
      );
    }
    return (
      <div key={groupIndex} className={styles.inputGroup}>
        {inputs}
      </div>
    );
  };

  let currentIndex = 0;
  const groups = GROUP_SIZES.map((size, groupIndex) => {
    const group = renderInputGroup(currentIndex, size, groupIndex);
    currentIndex += size;
    return group;
  });

  return (
    <div className={classNames(styles.container, className)}>
      <OrganizationMembershipCodeGroups
        groups={groups}
        separatorClassName={styles.separator}
      />
    </div>
  );
};

export default OrganizationMembershipCodeSplitInput;
